import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { useThemeStore } from "../../../store";
import { useTheme } from "../../../hooks";
import { themes } from "../../../config/thems";
import { useMemo } from "react";
import Common from "../../../helper/common";
import LottieEmptyState from "./no-data-animation/index";

const CourtWiseRevenue = ({ sportCourtRevenue, selectedSport, height = 350 }) => {
  const { theme: config, setTheme: setConfig } = useThemeStore();
  const { theme: mode } = useTheme();

  const theme = themes.find((theme) => theme.name === config);

  // Parse JSON data
  const parsedData = useMemo(() => {
    if (!sportCourtRevenue) return [];
    try {
      return typeof sportCourtRevenue === "string"
        ? JSON.parse(sportCourtRevenue)
        : sportCourtRevenue;
    } catch {
      return [];
    }
  }, [sportCourtRevenue]);

  // Prepare chart data for Recharts
  const chartData = useMemo(() => {
    if (!Array.isArray(parsedData) || parsedData.length === 0 || !selectedSport) {
      return [];
    }

    const sportData = parsedData.find((s) => s.sport === selectedSport);
    
    if (!sportData || !sportData.courts) {
      return [];
    }

    const courts = Object.keys(sportData.courts || {});
    const values = courts.map((courtName) => sportData.courts[courtName] || 0);

    // Create data array for Recharts
    const data = courts.map((courtName, index) => ({
      court: courtName,
      value: values[index],
      formattedValue: Common.Utility.formatNumber(values[index])
    }));
    
    return data;
  }, [parsedData, selectedSport]);
  // Colors for different bars
  const colors = [
    `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].primary})`,
    `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].info})`,
    `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].success})`,
    `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].warning})`,
    "#FF6B6B",
    "#6C5CE7",
    "#00B894",
    "#E17055",
  ];

  if (!Array.isArray(chartData) || chartData.length === 0) {
    return (
      <LottieEmptyState
        title="No Court Data"
        subtitle={
          selectedSport 
            ? `No courts found for ${selectedSport}. Please select a different sport.`
            : "Please select a sport to view court data."
        }
        height={height}
        width={200}
      />
    );
  }

  return (
    <div style={{ width: "100%", height: height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
        >
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke={`hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].chartGird})`}
          />
          <XAxis 
            dataKey="court"
            tick={{ 
              fontSize: 13, 
              fontWeight: 500,
              fill: `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].chartLabel})`,
              angle: -45,
              textAnchor: 'end',
              height: 60
            }}
            axisLine={{ stroke: `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].chartGird})` }}
            tickLine={{ stroke: `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].chartGird})` }}
          />
          <YAxis 
            type="number"
            domain={[0, 'dataMax']}
            tickFormatter={(value) => Common.Utility.formatNumber(value)}
            tick={{ 
              fontSize: 12, 
              fill: `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].dark})` 
            }}
            axisLine={{ stroke: `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].chartGird})` }}
            tickLine={{ stroke: `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].chartGird})` }}
            label={{ 
              value: 'Price Margin', 
              angle: -90, 
              position: 'insideLeft',
              style: { textAnchor: 'middle', fontSize: '13px', fontWeight: 600, fill: `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].chartLabel})` }
            }}
          />
          <Tooltip 
            formatter={(value) => [Common.Utility.formatNumber(value), "Price Margin"]}
            labelFormatter={(label) => `Court: ${label}`}
            contentStyle={{
              backgroundColor: mode === "dark" ? "#1f2937" : "#ffffff",
              border: `1px solid ${theme?.cssVars[mode === "dark" ? "dark" : "light"].chartGird}`,
              borderRadius: "6px",
            }}
          />
          <Bar 
            dataKey="value" 
            radius={[4, 4, 0, 0]}
            maxBarSize={60}
            fill={`hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].primary})`}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CourtWiseRevenue;